import React, { useEffect } from 'react'
import { useSetState } from 'react-use'
import { TransferList } from 'components/ui-layouts/transferList/TransferList'
import { showToast } from 'components/ui-kit/toastr/Toastr'
import { PurposeDTO } from 'interfaces/purposes/purpose'
import { DataCategory } from 'interfaces/dataCategory/dataCategory'
import { Box, Typography } from '@mui/material'
import { Button, PopUp } from '@ketch-com/deck'
import { useUpdatePurpose } from 'api/purposes/mutations/useUpdatePurpose'
import { ApiQueryKeys } from 'api/common/queryKeys'
import { useQueryClient } from 'react-query'
import { mapUpdatePurposeDataCategoriesToPayload } from 'pages/policyCenter/purposes/upsert/utils/mapCreatePurposeFormValuesToPayload'

type Props = {
  onSubmit?: () => void
  onCancel: () => void
  purpose?: PurposeDTO
  dataCategories?: DataCategory[]
  isLoading?: boolean
}

type State = {
  mappedData: any[]
  availableData: any[]
  mappedCookiesSearchString: string
  availableCookiesSearchString: string
}

// NOTE :: this is a demo component (to be removed in future)
// Relates to purposes -> view -> data -> map data demo page
export const PurposeDataCategoriesModal: React.FC<Props> = ({
  onSubmit,
  onCancel,
  purpose,
  dataCategories,
  isLoading,
}) => {
  const [state, setState] = useSetState<State>({
    mappedData: [],
    availableData: [],
    mappedCookiesSearchString: '',
    availableCookiesSearchString: '',
  })

  const { mutateAsync: handleUpdatePurpose, isLoading: isUpdatingPurpose } = useUpdatePurpose()
  const queryClient = useQueryClient()
  const isPending = isUpdatingPurpose || queryClient.isFetching() > 0

  const handleTransferListOnChange = (nextIncludedItems: any, item: DataCategory, method: string) => {
    // set mapped data based on response
    setState({ mappedData: nextIncludedItems })
    // remove item from available data if item is added else push
    if (method === 'INCLUDE') {
      setState({ availableData: state.availableData?.filter(data => data.code !== item.code) })
    } else {
      setState({ availableData: [...state.availableData, item] })
    }
  }

  const mappedList = state.mappedData?.filter(data =>
    data.name.toLowerCase().includes(state.mappedCookiesSearchString.toLowerCase()),
  )

  const availableList = state.availableData?.filter(data =>
    data.name.toLowerCase().includes(state.availableCookiesSearchString.toLowerCase()),
  )

  useEffect(() => {
    if (!isLoading && purpose && dataCategories?.length) {
      // Set initial state once all data is loaded
      setState({
        mappedData: purpose?.dataCategories || [],
        availableData:
          dataCategories?.filter(
            data => !purpose.dataCategories?.some(purposeDataCategory => purposeDataCategory.ID === data.ID),
          ) || [],
      })
    }
  }, [isLoading, purpose, dataCategories, setState])

  const onSubmitClick = async () => {
    if (!purpose) return

    const formData = mapUpdatePurposeDataCategoriesToPayload(mappedList, purpose)

    try {
      const { data } = await handleUpdatePurpose({
        params: {
          purposeCode: purpose.code,
          formData,
        },
      })

      await queryClient.refetchQueries(ApiQueryKeys.entitlements)
      await queryClient.refetchQueries([ApiQueryKeys.purpose, { purposeCode: data.purpose.code }])

      showToast({ content: 'Data categories updated', type: 'success' })
    } catch (e) {
      showToast({ content: 'Failed to update data categories', type: 'error' })
    }

    onSubmit?.()
  }

  return (
    <PopUp
      variant="modal"
      title="Update Data Categories"
      onClose={onCancel}
      popUpWidth="950px"
      popUpActionChildren={
        <>
          <Button size="large" color="tertiary" onClick={onCancel} pending={isPending}>
            Cancel
          </Button>
          <Button size="large" onClick={onSubmitClick} pending={isPending}>
            Update
          </Button>
        </>
      }
    >
      <TransferList
        excludedTitle="All Data Categories"
        excludedItems={availableList}
        excludedItemsSearchString={state.availableCookiesSearchString}
        includedTitle="Selected Data Categories"
        includedItems={mappedList}
        includedItemsSearchString={state.mappedCookiesSearchString}
        onChange={handleTransferListOnChange}
        onSearchIncludedItems={nextSearchString => setState({ mappedCookiesSearchString: nextSearchString })}
        onSearchExcludedItems={nextSearchString => setState({ availableCookiesSearchString: nextSearchString })}
        itemRenderer={data => (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'flex-start',
            }}
          >
            <Typography>{data.name}</Typography>
            <Typography variant="footnote">{data.code}</Typography>
          </Box>
        )}
      />
    </PopUp>
  )
}
